import {createSelector, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {v4 as uuidv4} from 'uuid';
import {OrderDNATestFormConfig} from "@/features/OrderDNATestForm/Config/OrderDNATestFormConfig";
import {LoadingStatus} from "@/store/LoadingStatus";
import {PriceListServiceInterface} from "@/api/Orders/PriceListServices/Interfaces/PriceListServiceInterface";
import {
    OrderAdditionalServiceInterface
} from "@/api/List/OrderAdditionalServices/Interfaces/OrderAdditionalServiceInterface";
import {RootState} from "@/store/Store";
import {
    CalculateSelectionsInterface
} from "@/api/Orders/NewCalculatePrice/CalculateSelections/CalculateSelectionsApiSlice";
import {SampleInterface} from "@/api/Samples/GetAll/Interfaces/SampleInterface";
import {infiniteCount} from "@/config/Other";
import {
    AttributeRuleForNParticipantsFlatItemInterface
} from "@/api/ReferenceBooks/AttributeRuleForNParticipants/List/Interfaces/AttributeRuleForNParticipantsListInterface";
import {
    FamilyStepsActiveItemInterface
} from "@/api/ReferenceBooks/FamilySteps/List/Interfaces/FamilyStepsListInterface";
import {
    getTestTypeAlias,
    isFieldEnabledByRule,
} from "@/features/OrderDNATestForm/Helpers/orderFormFieldRules";
import {getRequiredSampleValidation} from "@/features/OrderDNATestForm/Helpers/requiredSampleValidation";
import {OrderFormFieldRuleInterface} from "@/api/ReferenceBooks/Rules/AnalysisOrderForm/OrderFormFields";
import {hasNonStandardSample} from "@/features/Orders/helpers/nonStandardSample";
import {isKinshipCategoryFlowEnabled} from "@/features/OrderDNATestForm/Helpers/kinshipCategoryFlow";
import {PedigreeSchema} from "@/features/PedigreeEditor";
import type {ParticipantSampleInterface} from "@/common/Orders/ParticipantSamples/ParticipantSampleTypes";
import {areParticipantSamplePartsValid} from "@/common/Orders/ParticipantSamples/ParticipantSamplePartsValidation";
import {

    getInvalidParticipantSamples
} from "@/common/Orders/ParticipantSamples/ParticipantSampleNormalization";

export const notSelectedId = -1;

// TODO: check interfaces
// export interface SampleTypeInterface {
//     id: number,
//     is_payed: boolean,
//     sequence: number,
//     alias?: string | null,
//     requires_nonstandard_consent?: boolean,
// }
export type SampleTypeInterface = ParticipantSampleInterface;

export type OrderDNATestFormState = ReturnType<typeof getInitialState>;

export type AllOrderStepsFields =
    keyof OrderDNATestFormState["step1"]
    | keyof OrderDNATestFormState["step2"]
    | keyof OrderDNATestFormState["step3"];

type KeysWithValue<T> = {
    [K in keyof T]: T[K] extends { value: unknown } ? K : never;
}[keyof T];

// Типы для каждого шага (для удобства)
type Step1 = OrderDNATestFormState["step1"];
type Step2 = OrderDNATestFormState["step2"];
type Step3 = OrderDNATestFormState["step3"];

export type AllStepsFieldsWithValue = KeysWithValue<Step1> | KeysWithValue<Step2> | KeysWithValue<Step3>

// Discriminated union для payload: для каждого step + name + value точный тип
export type SetFormValuePayload =
    | {
    [K in KeysWithValue<Step1>]: {
        step: "step1";
        name: K;
        value?: Step1[K]["value"];
        error?: Step1[K]["error"];
        valid?: Step1[K]["valid"];
    }
}[KeysWithValue<Step1>]
    | {
    [K in KeysWithValue<Step2>]: {
        step: "step2";
        name: K;
        value?: Step2[K]["value"];
        error?: Step2[K]["error"];
        valid?: Step2[K]["valid"];
    }
}[KeysWithValue<Step2>]
    | {
    [K in KeysWithValue<Step3>]: {
        step: "step3";
        name: K;
        value?: Step3[K]["value"];
        error?: Step3[K]["error"];
        valid?: Step3[K]["valid"];
    }
}[KeysWithValue<Step3>];

function getInitialState() {
    return {
        step: 0,
        highlightedField: null as string | null,

        initialSelections: null as CalculateSelectionsInterface | null,
        orderFormFieldRules: [] as OrderFormFieldRuleInterface[],
        orderPrice: null as number | null,
        participantsMin: infiniteCount as number | null,
        participantsMax: infiniteCount as number | null,
        sampleMin: infiniteCount as number | null,
        sampleMax: infiniteCount as number | null,

        step1: {
            orderId: {
                value: uuidv4(),
                valid: true,
                error: "",
            },
            partner: {
                value: notSelectedId,
                valid: false,
                error: "",
            },
            testType: {
                value: "",
                valid: false,
                error: "",
            },
            testTypeOther: {
                value: "",
                valid: false,
                error: "",
            },
            additionalServices: {
                value: [] as OrderAdditionalServiceInterface["id"][],
                valid: false,
                error: "",
            },
            legalStatus: {
                value: "",
                valid: false,
                error: "",
            },
            attribute: {
                value: "",
                valid: false,
                error: "",
            },
            researchPeriod: {
                value: "",
                valid: false,
                error: "",
            },
            /** @deprecated */
            priceListServices: {
                value: [] as PriceListServiceInterface["id"][],
                valid: true,
                error: "",
            },
        },
        step2: {
            participantId: "",
            samplesVariants: [] as SampleInterface[],
            isSelectionsFetching: false,
            isSelectionsSuccess: false,
            profileResearch: {
                value: notSelectedId,
                valid: false,
                error: "",
            },
            fioOrNickname: {
                value: "",
                valid: false,
                error: "",
            },
            gender: {
                value: notSelectedId,
                valid: false,
                error: "",
            },
            birthday: {
                value: "",
                valid: true,
                error: "",
            },
            degreeOfKinship: {
                value: notSelectedId,
                valid: false,
                error: "",
            },
            degreeOfKinshipPreviousOrderNumber: {
                value: "",
                valid: false,
                error: "",
            },
            sampleType: {
                value: [] as SampleTypeInterface[],
                valid: false,
                error: "",
            },
            sampleTypeNotInWork: {
                value: [] as SampleTypeInterface[],
                valid: true,
                error: "",
            },
            sampleTypeOther: {
                value: "",
                valid: false,
                error: "",
            },
            documentType: {
                value: notSelectedId,
                valid: false,
                error: "",
            },
            documentNumber: {
                value: "",
                valid: false,
                error: "",
            },
            sampleCollectionDate: {
                value: "",
                valid: true,
                error: "",
            },
            whoCollectedSamples: {
                value: notSelectedId,
                valid: false,
                error: "",
            },
            isExternalSampling: {
                value: false,
                valid: true,
                error: "",
            },
            commentForAllSamples: {
                value: "",
                valid: false,
                error: "",
            },
            participantPhotos: {
                value: [] as UploadingFileInterface[],
                valid: false,
                error: "",
            },
            identityDocuments: {
                value: [] as UploadingFileInterface[],
                valid: false,
                error: "",
            },
            informedConsentDocuments: {
                value: [] as UploadingFileInterface[],
                valid: false,
                error: "",
            },
            readyProfile: {
                value: [] as UploadingFileInterface[],
                valid: false,
                error: "",
            },

            isUserNewOrEdit: "new" as userNewOrEdit,

            testParticipants: [] as TestParticipantInterface[],

            isFormVisible: false,

            editingParticipantId: "",
        },
        step3: {
            isThirdStepValid: true,

            //additional info
            firstTestType: {
                value: "",
                valid: false,
                error: "",
            },
            pregnancyPeriod: {
                value: "",
                valid: false,
                error: "",
            },
            pregnancyPeculiarities: {
                value: "notChosen",
                valid: false,
                error: "",
            },
            describeKinshipTies: {
                value: "",
                valid: false,
                error: "",
            },
            anotherImportantInfo: {
                value: "",
                valid: false,
                error: "",
            },
            trackNumber: {
                value: "",
                valid: false,
                error: "",
            },
            civilCaseNumber: {
                value: "",
                valid: false,
                error: "",
            },
            courtName: {
                value: "",
                valid: false,
                error: "",
            },

            files: [
                {
                    id: uuidv4(),
                    file: null,
                    meaning: "",
                },
                {
                    id: uuidv4(),
                    file: null,
                    meaning: "",
                },
            ] as UploadingFileInterface[],

            matchingRules: [] as AttributeRuleForNParticipantsFlatItemInterface[],
            matchingRuleId: "notChosen",
            kinshipScheme: "",
            familyStepInDoubt: null as FamilyStepsActiveItemInterface | null,
            kinshipSchemaJson: null as PedigreeSchema | null,

            // CategorySelector state
            categorySelectionPath: [] as Array<{ containerId: number; selectedItemId: number | null }>,
            currentCategoryStep: 0,
            finalSelectedRule: null as AttributeRuleForNParticipantsFlatItemInterface | null,
        },
        checkedOrder: {
            value: false,
            valid: false,
            error: "",
        },
        kinshipSchemaConfirmed: false,
        orderSendingStatus: LoadingStatus.idle,
    }
}

function clearKinshipRuleFields(state: OrderDNATestFormState) {
    state.step3.matchingRuleId = "notChosen";
    state.step3.kinshipScheme = "";
    state.step3.familyStepInDoubt = null;
}

export const OrderDNATestFormSlice = createSlice({
    name: "OrderDNATestFormSlice",
    initialState: getInitialState(),
    reducers: {
        setInitialSelections: (state, action: PayloadAction<CalculateSelectionsInterface | null>) => {
            state.initialSelections = action.payload;
        },
        setOrderFormFieldRules: (state, action: PayloadAction<OrderFormFieldRuleInterface[]>) => {
            state.orderFormFieldRules = action.payload;
        },
        setParticipantsMin: (state, action: PayloadAction<number | null>) => {
            state.participantsMin = action.payload;
        },
        setParticipantsMax: (state, action: PayloadAction<number | null>) => {
            state.participantsMax = action.payload;
        },
        setSampleMin: (state, action: PayloadAction<number | null>) => {
            state.sampleMin = action.payload;
        },
        setSampleMax: (state, action: PayloadAction<number | null>) => {
            state.sampleMax = action.payload;
        },
        setOrderPrice: (state, action: PayloadAction<number | null>) => {
            state.orderPrice = action.payload;
        },
        setSamplesVariants: (state, action: PayloadAction<SampleInterface[]>) => {
            state.step2.samplesVariants = action.payload;
        },
        setSelectionsFetching: (state, action: PayloadAction<boolean>) => {
            state.step2.isSelectionsFetching = action.payload;
        },
        setSelectionsSuccess: (state, action: PayloadAction<boolean>) => {
            state.step2.isSelectionsSuccess = action.payload;
        },
        setStep: (state, action: PayloadAction<number>) => {
            state.step = action.payload;
        },
        setHighlightedField: (state, action: PayloadAction<string | null>) => {
            state.highlightedField = action.payload;
        },

        setFormValue: (state: OrderDNATestFormState, action: PayloadAction<SetFormValuePayload>) => {
            const {step, name, value} = action.payload;

            switch (step) {
                case "step1":
                    if (value !== undefined) state.step1[name].value = value;
                    break;
                case "step2":
                    if (value !== undefined) state.step2[name].value = value;
                    break;
                case "step3":
                    if (value !== undefined) state.step3[name].value = value;
                    break;
            }
        },
        setParticipantSampleLists: (state, action: PayloadAction<{
            sampleType: SampleTypeInterface[];
            sampleTypeNotInWork: SampleTypeInterface[];
        }>) => {
            state.step2.sampleType.value = action.payload.sampleType;
            state.step2.sampleTypeNotInWork.value = action.payload.sampleTypeNotInWork;
        },
        clearParticipantSamples: (state) => {
            state.step2.sampleType.value = [];
            state.step2.sampleTypeNotInWork.value = [];
            state.step2.sampleTypeOther.value = "";
            state.step2.testParticipants = state.step2.testParticipants.map(participant => ({
                ...participant,
                sampleType: [],
                sampleTypeNotInWork: [],
                sampleTypeOther: "",
            }));
        },
        setFormError: (state: OrderDNATestFormState, action: PayloadAction<SetFormValuePayload>) => {
            const {step, name, error} = action.payload;

            switch (step) {
                case "step1":
                    if (error !== undefined) state.step1[name].error = error;
                    break;
                case "step2":
                    if (error !== undefined) state.step2[name].error = error;
                    break;
                case "step3":
                    if (error !== undefined) state.step3[name].error = error;
                    break;
            }
        },
        setFormValidation: (state: OrderDNATestFormState, action: PayloadAction<SetFormValuePayload>) => {
            const {step, name, valid} = action.payload;

            switch (step) {
                case "step1":
                    if (valid !== undefined) state.step1[name].valid = valid;
                    break;
                case "step2":
                    if (valid !== undefined) state.step2[name].valid = valid;
                    break;
                case "step3":
                    if (valid !== undefined) state.step3[name].valid = valid;
                    break;
            }
        },
        setCheckedOrder: (state, action: PayloadAction<boolean>) => {
            state.checkedOrder.value = action.payload;
        },
        setKinshipSchemaConfirmed: (state, action: PayloadAction<boolean>) => {
            state.kinshipSchemaConfirmed = action.payload;
        },
        showParticipantForm: (state) => {
            state.step2.isFormVisible = true;
        },
        hideParticipantForm: (state) => {
            state.step2.isFormVisible = false;
        },
        addTestParticipant: (state, action) => {
            const id = uuidv4();
            const fioOrNickname = state.step2.fioOrNickname.value;
            const gender = state.step2.gender.value;
            const birthday = state.step2.birthday.value;
            const degreeOfKinship = state.step2.degreeOfKinship.value;
            const degreeOfKinshipPreviousOrderNumber = state.step2.degreeOfKinshipPreviousOrderNumber.value;
            const sampleType = state.step2.sampleType.value;
            const sampleTypeNotInWork = state.step2.sampleTypeNotInWork.value;
            const sampleTypeOther = state.step2.sampleTypeOther.value;
            const sampleCollectionDate = state.step2.sampleCollectionDate.value;
            const whoCollectedSamples = state.step2.whoCollectedSamples.value;
            const isExternalSampling = state.step2.isExternalSampling.value;
            const documentType = state.step2.documentType.value;
            const documentNumber = state.step2.documentNumber.value;
            const participantPhotos = state.step2.participantPhotos.value;
            const identityDocuments = state.step2.identityDocuments.value;
            const informedConsentDocuments = state.step2.informedConsentDocuments.value;
            const readyProfile = state.step2.readyProfile.value;
            const profileResearch = state.step2.profileResearch.value;

            state.step2.testParticipants.push({
                id, fioOrNickname, gender, birthday, degreeOfKinship,
                degreeOfKinshipPreviousOrderNumber, sampleType, sampleTypeNotInWork,
                sampleTypeOther, sampleCollectionDate, whoCollectedSamples,
                documentType, documentNumber,
                participantPhotos, identityDocuments, informedConsentDocuments, readyProfile,
                profileResearch,
                isExternalSampling,
            });
        },
        deleteTestParticipant: (state, action) => {
            state.step2.testParticipants = state.step2.testParticipants.filter(participant => participant.id !== action.payload.id);
            clearKinshipRuleFields(state);
        },
        editTestParticipant: (state, action) => {
            const id = action.payload.id;
            const participant = state.step2.testParticipants.find(participant => participant.id === id) as TestParticipantInterface;
            state.step2.editingParticipantId = participant.id;
            state.step2.participantId = participant.id;
            state.step2.fioOrNickname.value = participant.fioOrNickname;
            state.step2.gender.value = participant.gender;
            state.step2.birthday.value = participant.birthday;
            state.step2.degreeOfKinship.value = participant.degreeOfKinship;
            state.step2.degreeOfKinshipPreviousOrderNumber.value = participant.degreeOfKinshipPreviousOrderNumber;
            state.step2.sampleType.value = participant.sampleType;
            state.step2.sampleTypeNotInWork.value = participant.sampleTypeNotInWork;
            state.step2.sampleTypeOther.value = participant.sampleTypeOther;
            state.step2.sampleCollectionDate.value = participant.sampleCollectionDate;
            state.step2.whoCollectedSamples.value = participant.whoCollectedSamples;
            state.step2.isExternalSampling.value = participant.isExternalSampling;
            state.step2.documentType.value = participant.documentType;
            state.step2.documentNumber.value = participant.documentNumber;
            state.step2.participantPhotos.value = participant.participantPhotos;
            state.step2.identityDocuments.value = participant.identityDocuments;
            state.step2.informedConsentDocuments.value = participant.informedConsentDocuments;
            state.step2.readyProfile.value = participant.readyProfile;
            state.step2.profileResearch.value = participant.profileResearch;
        },
        makeParticipantFormValid: (state, action) => {
            state.step2.fioOrNickname.valid = true;
            state.step2.birthday.valid = true;
            state.step2.degreeOfKinship.valid = true;
            state.step2.degreeOfKinshipPreviousOrderNumber.valid = state.step2.degreeOfKinshipPreviousOrderNumber.value !== "";
            state.step2.sampleType.valid = true;
            state.step2.sampleTypeOther.valid = state.step2.sampleTypeOther.value !== "";
            state.step2.whoCollectedSamples.valid = true;
        },
        setParticipantFormStatus: (state, action) => {
            state.step2.isUserNewOrEdit = action.payload.status;
        },
        saveEditedTestParticipant: (state, action) => {
            state.step2.testParticipants = state.step2.testParticipants.map(participant => {
                if (participant.id !== state.step2.participantId) {
                    return participant;
                }
                const editedParticipant: TestParticipantInterface = {
                    id: participant.id,
                    fioOrNickname: state.step2.fioOrNickname.value,
                    gender: state.step2.gender.value,
                    birthday: state.step2.birthday.value,
                    degreeOfKinship: state.step2.degreeOfKinship.value,
                    degreeOfKinshipPreviousOrderNumber: state.step2.degreeOfKinshipPreviousOrderNumber.value,
                    sampleType: state.step2.sampleType.value,
                    sampleTypeNotInWork: state.step2.sampleTypeNotInWork.value,
                    sampleTypeOther: state.step2.sampleTypeOther.value,
                    sampleCollectionDate: state.step2.sampleCollectionDate.value,
                    whoCollectedSamples: state.step2.whoCollectedSamples.value,
                    documentType: state.step2.documentType.value,
                    documentNumber: state.step2.documentNumber.value,
                    participantPhotos: state.step2.participantPhotos.value,
                    identityDocuments: state.step2.identityDocuments.value,
                    informedConsentDocuments: state.step2.informedConsentDocuments.value,
                    readyProfile: state.step2.readyProfile.value,
                    profileResearch: state.step2.profileResearch.value,
                    isExternalSampling: state.step2.isExternalSampling.value,
                };
                return editedParticipant;
            });
        },
        cancelEditingParticipant: (state) => {
            state.step2.editingParticipantId = "";
        },
        addDocumentRow: (state, action) => {
            if (state.step3.files.length === OrderDNATestFormConfig.maxDocumentsCount) {
                return;
            }
            const doc: UploadingFileInterface = {
                id: uuidv4(),
                file: null,
                meaning: "",
            };
            state.step3.files.push(doc);
        },
        deleteDocumentRow: (state, action) => {
            const id = action.payload.id;
            state.step3.files = state.step3.files.filter(file => file.id !== id);
        },
        setFileInfo: (state, action) => {
            const id = action.payload.id;
            const {name, size} = action.payload.file;
            state.step3.files = state.step3.files.map(fileInSlice => {
                if (fileInSlice.id !== id) {
                    return fileInSlice;
                }
                fileInSlice.file = {name, size};
                return fileInSlice;
            })
        },
        setFileMeaning: (state, action) => {
            const {id, value} = action.payload;
            state.step3.files = state.step3.files.map(fileInSlice => {
                if (fileInSlice.id !== id) {
                    return fileInSlice;
                }
                fileInSlice.meaning = value;
                return fileInSlice;
            })
        },
        addParticipantPhoto: (state, action) => {
            const photo: UploadingFileInterface = {
                id: action.payload.id,
                file: action.payload.file,
                meaning: "photo",
            };
            state.step2.participantPhotos.value.push(photo);
        },
        deleteParticipantPhoto: (state, action) => {
            const id = action.payload.id;
            state.step2.participantPhotos.value = state.step2.participantPhotos.value.filter(photo => photo.id !== id);
        },
        addIdentityDocument: (state, action) => {
            const document: UploadingFileInterface = {
                id: action.payload.id,
                file: action.payload.file,
                meaning: "identity_document",
            };
            state.step2.identityDocuments.value.push(document);
        },
        deleteIdentityDocument: (state, action) => {
            const id = action.payload.id;
            state.step2.identityDocuments.value = state.step2.identityDocuments.value.filter(document => document.id !== id);
        },
        addInformedConsentDocument: (state, action) => {
            const document: UploadingFileInterface = {
                id: action.payload.id,
                file: action.payload.file,
                meaning: "informedConsent",
            };
            state.step2.informedConsentDocuments.value.push(document);
        },
        deleteInformedConsentDocument: (state, action) => {
            const id = action.payload.id;
            state.step2.informedConsentDocuments.value = state.step2.informedConsentDocuments.value.filter(document => document.id !== id);
        },
        addReadyProfile: (state, action) => {
            const readyProfile: UploadingFileInterface = {
                id: action.payload.id,
                file: action.payload.file,
                meaning: "ready_profile",
            };
            state.step2.readyProfile.value.push(readyProfile);
        },
        deleteReadyProfile: (state, action) => {
            const id = action.payload.id;
            state.step2.readyProfile.value = state.step2.readyProfile.value.filter(document => document.id !== id);
        },
        clearAll: (state, action: PayloadAction<void>) => {
            const newInitialState = getInitialState();
            state.step = newInitialState.step;
            state.initialSelections = newInitialState.initialSelections;
            state.orderPrice = newInitialState.orderPrice;
            state.participantsMin = newInitialState.participantsMin;
            state.participantsMax = newInitialState.participantsMax;
            state.sampleMin = newInitialState.sampleMin;
            state.sampleMax = newInitialState.sampleMax;
            state.step1 = newInitialState.step1;
            state.step2 = newInitialState.step2;
            state.step3 = newInitialState.step3;
            state.orderSendingStatus = newInitialState.orderSendingStatus;
            state.checkedOrder = newInitialState.checkedOrder;
        },
        setSendingStatus: (state, action) => {
            state.orderSendingStatus = action.payload.loadingStatus;
        },
        clearStep1: (state, action: PayloadAction<void>) => {
            const newInitialState = getInitialState();
            state.step1.testType = newInitialState.step1.testType;
            state.participantsMin = newInitialState.participantsMin;
            state.participantsMax = newInitialState.participantsMax;
            state.sampleMin = newInitialState.sampleMin;
            state.sampleMax = newInitialState.sampleMax;
            state.step1.testTypeOther = newInitialState.step1.testTypeOther;
            state.step1.additionalServices = newInitialState.step1.additionalServices;
            state.step1.legalStatus = newInitialState.step1.legalStatus;
            state.step1.attribute = newInitialState.step1.attribute;
            state.step1.researchPeriod = newInitialState.step1.researchPeriod;
            state.step3.firstTestType = newInitialState.step3.firstTestType;
            state.step3.pregnancyPeriod = newInitialState.step3.pregnancyPeriod;
            state.step3.describeKinshipTies = newInitialState.step3.describeKinshipTies;
        },
        clearTestType: (state, action: PayloadAction<void>) => {
            const newInitialState = getInitialState();
            state.step1.testType = newInitialState.step1.testType;
            state.participantsMin = newInitialState.participantsMin;
            state.participantsMax = newInitialState.participantsMax;
            state.sampleMin = newInitialState.sampleMin;
            state.sampleMax = newInitialState.sampleMax;
        },
        clearLegalStatus: (state, action: PayloadAction<void>) => {
            const newInitialState = getInitialState();
            state.step1.legalStatus = newInitialState.step1.legalStatus;
        },
        clearAttribute: (state, action: PayloadAction<void>) => {
            const newInitialState = getInitialState();
            state.step1.attribute = newInitialState.step1.attribute;
        },
        clearResearchPeriod: (state, action: PayloadAction<void>) => {
            const newInitialState = getInitialState();
            state.step1.researchPeriod = newInitialState.step1.researchPeriod;
        },
        clearAdditionalServices: (state, action: PayloadAction<void>) => {
            const newInitialState = getInitialState();
            state.step1.additionalServices = newInitialState.step1.additionalServices;
        },
        clearParticipantForm: (state, action: PayloadAction<void>) => {
            const newInitialState = getInitialState();
            state.step2.fioOrNickname = newInitialState.step2.fioOrNickname;
            state.step2.gender = newInitialState.step2.gender;
            state.step2.birthday = newInitialState.step2.birthday;
            state.step2.degreeOfKinship = newInitialState.step2.degreeOfKinship;
            state.step2.degreeOfKinshipPreviousOrderNumber = newInitialState.step2.degreeOfKinshipPreviousOrderNumber;
            state.step2.sampleType = newInitialState.step2.sampleType;
            state.step2.sampleTypeNotInWork = newInitialState.step2.sampleTypeNotInWork;
            state.step2.sampleTypeOther = newInitialState.step2.sampleTypeOther;
            state.step2.sampleCollectionDate = newInitialState.step2.sampleCollectionDate;
            state.step2.whoCollectedSamples = newInitialState.step2.whoCollectedSamples;
            state.step2.isExternalSampling = newInitialState.step2.isExternalSampling;
            state.step2.documentType = newInitialState.step2.documentType;
            state.step2.documentNumber = newInitialState.step2.documentNumber;
            state.step2.participantPhotos = newInitialState.step2.participantPhotos;
            state.step2.identityDocuments = newInitialState.step2.identityDocuments;
            state.step2.informedConsentDocuments = newInitialState.step2.informedConsentDocuments;
            state.step2.readyProfile = newInitialState.step2.readyProfile;
            state.step2.profileResearch = newInitialState.step2.profileResearch;
        },
        setMatchingRules: (state, action: PayloadAction<AttributeRuleForNParticipantsFlatItemInterface[]>) => {
            state.step3.matchingRules = action.payload;
        },
        setMatchingRuleId: (state, action: PayloadAction<string>) => {
            state.step3.matchingRuleId = action.payload;
        },
        clearAutomaticKinshipFields: (state) => {
            clearKinshipRuleFields(state);
            state.step1.attribute.value = "";
            state.step1.attribute.valid = false;
            state.step1.attribute.error = "";
            state.step3.describeKinshipTies.value = "";
            state.step3.describeKinshipTies.valid = false;
            state.step3.describeKinshipTies.error = "";
        },
        clearAutomaticKinshipRuleFields: (state) => {
            clearKinshipRuleFields(state);
        },
        disableKinshipCategoryFlow: (state) => {
            state.step3.matchingRules = [];
            state.step3.matchingRuleId = "notChosen";
            state.step3.kinshipScheme = "";
            state.step3.familyStepInDoubt = null;
            state.step3.kinshipSchemaJson = null;
            state.step3.categorySelectionPath = [];
            state.step3.currentCategoryStep = 0;
            state.step3.finalSelectedRule = null;
            state.step3.describeKinshipTies.value = "";
            state.step3.describeKinshipTies.valid = false;
            state.step3.describeKinshipTies.error = "";
            state.kinshipSchemaConfirmed = false;
        },
        updateMatchingRule: (state, action: PayloadAction<updateMatchingRulePayloadAction>) => {
            state.step3.kinshipScheme = action.payload.kinshipScheme;
            state.step3.familyStepInDoubt = action.payload.familyStepInDoubt;
            state.step1.attribute.value = action.payload.attribute;
            state.step1.attribute.error = "";
            state.step1.attribute.valid = true;
        },

        // CategorySelector actions
        selectCategoryItem: (state, action: PayloadAction<{ containerId: number; itemId: number }>) => {
            const {containerId, itemId} = action.payload;
            // Обновляем или добавляем шаг
            const existingStepIndex = state.step3.categorySelectionPath.findIndex(
                step => step.containerId === containerId
            );
            if (existingStepIndex >= 0) {
                state.step3.categorySelectionPath[existingStepIndex].selectedItemId = itemId;
                // Удаляем все шаги после этого (перевыбор)
                state.step3.categorySelectionPath = state.step3.categorySelectionPath.slice(0, existingStepIndex + 1);
            } else {
                state.step3.categorySelectionPath.push({containerId, selectedItemId: itemId});
            }
        },
        goToNextCategoryStep: (state) => {
            state.step3.currentCategoryStep++;
        },
        goToPrevCategoryStep: (state) => {
            if (state.step3.currentCategoryStep > 0) {
                state.step3.currentCategoryStep--;
            }
            state.step3.finalSelectedRule = null;
        },
        resetCategorySelection: (state) => {
            state.step3.categorySelectionPath = [];
            state.step3.currentCategoryStep = 0;
            state.step3.finalSelectedRule = null;
            state.step3.kinshipSchemaJson = null;
            clearKinshipRuleFields(state);
            state.step1.attribute.value = "";
            state.step1.attribute.valid = false;
            state.step1.attribute.error = "";
        },
        setFinalRule: (state, action: PayloadAction<AttributeRuleForNParticipantsFlatItemInterface>) => {
            const rule = action.payload;
            state.step3.finalSelectedRule = rule;
            // Автоматически подставляем значения как раньше
            state.step3.kinshipScheme = rule.question?.schema ?? "";
            state.step3.familyStepInDoubt = rule.question?.family_step ?? null;
            state.step3.kinshipSchemaJson = rule.question?.pedigree_schema_json ?? null;
            state.step1.attribute.value = rule.question?.research_sign?.id
                ? String(rule.question.research_sign.id)
                : "";
            state.step1.attribute.valid = true;
            state.step1.attribute.error = "";
        },
    },
});

export const {
    setInitialSelections,
    setOrderFormFieldRules,
    setParticipantsMin,
    setParticipantsMax,
    setSampleMin,
    setSampleMax,
    setOrderPrice,
    setSamplesVariants,
    setSelectionsFetching,
    setSelectionsSuccess,
    setStep,
    setFormValue,
    setParticipantSampleLists,
    clearParticipantSamples,
    setFormError,
    setFormValidation,
    setCheckedOrder,
    setKinshipSchemaConfirmed,
    showParticipantForm,
    hideParticipantForm,
    addTestParticipant,
    clearParticipantForm,
    deleteTestParticipant,
    editTestParticipant,
    makeParticipantFormValid,
    setParticipantFormStatus,
    saveEditedTestParticipant,
    cancelEditingParticipant,
    addDocumentRow,
    deleteDocumentRow,
    setFileInfo,
    setFileMeaning,
    addParticipantPhoto,
    deleteParticipantPhoto,
    addIdentityDocument,
    deleteIdentityDocument,
    addInformedConsentDocument,
    deleteInformedConsentDocument,
    addReadyProfile,
    deleteReadyProfile,
    clearAll,
    setSendingStatus,
    clearStep1,
    clearTestType,
    clearLegalStatus,
    clearAttribute,
    clearResearchPeriod,
    clearAdditionalServices,
    setMatchingRules,
    setMatchingRuleId,
    clearAutomaticKinshipFields,
    clearAutomaticKinshipRuleFields,
    disableKinshipCategoryFlow,
    updateMatchingRule,
    setHighlightedField,
    // CategorySelector exports
    selectCategoryItem,
    goToNextCategoryStep,
    goToPrevCategoryStep,
    resetCategorySelection,
    setFinalRule,
} = OrderDNATestFormSlice.actions;

//@ts-ignore
export const getSelectorForStep = state => state.orderDNATestForm.step;

export const getSelectorForValue = <
    S extends "step1" | "step2" | "step3",
    K extends keyof OrderDNATestFormState[S]
>(
    step: S,
    name: K
) => (state: RootState) => {
    const field = state.orderDNATestForm[step][name] as {
        value: OrderDNATestFormState[S][K] extends { value: infer V } ? V : never
    };
    return field?.value;
};

export const getSelectorForError = <
    S extends "step1" | "step2" | "step3",
    K extends keyof OrderDNATestFormState[S]
>(
    step: S,
    name: K
) => (state: RootState) => {
    const field = state.orderDNATestForm[step][name] as {
        error: OrderDNATestFormState[S][K] extends { error: infer V } ? V : never
    };
    return field?.error;
};

export const getSelectorForValidation = <
    S extends "step1" | "step2" | "step3",
    K extends keyof OrderDNATestFormState[S]
>(
    step: S,
    name: K
) => (state: RootState) => {
    const field = state.orderDNATestForm[step][name] as {
        valid: OrderDNATestFormState[S][K] extends { valid: infer V } ? V : never
    };
    return field?.valid;
};
export const highlightedFieldSelector = (state: RootState): string | null => state.orderDNATestForm.highlightedField;

export const getSelectorForStep2FormVisibility = (state: RootState) => state.orderDNATestForm.step2.isFormVisible

export const getSelectorForEditingParticipantId = (state: RootState) => state.orderDNATestForm.step2.editingParticipantId

export const getSelectorForParticipants = (state: RootState) => state.orderDNATestForm.step2.testParticipants;

export const requiredSampleValidationSelector = createSelector(
    [
        (state: RootState) => state.orderDNATestForm.step2.testParticipants,
        (state: RootState) => state.orderDNATestForm.participantsMin,
        (state: RootState) => state.orderDNATestForm.sampleMin,
    ],
    (participants, participantsMin, sampleMin) => getRequiredSampleValidation({
        participants,
        participantsMin,
        sampleMin,
    }),
);

export const isFirstStepValidSelector = (state: RootState) => {
    const testTypeValid = state.orderDNATestForm.step1.testType.valid;
    const testTypeOtherValid = state.orderDNATestForm.step1.testTypeOther.valid;
    const orderFormFieldRules = state.orderDNATestForm.orderFormFieldRules;

    const testTypeValidation = (testTypeValid && state.orderDNATestForm.step1.testType.value !== "testTypeOther") || (testTypeValid && testTypeOtherValid);
    const legalStatusValid = state.orderDNATestForm.step1.legalStatus.valid;
    const researchPeriodValid = state.orderDNATestForm.step1.researchPeriod.valid;
    const pregnancyPeriodRequired = isFieldEnabledByRule("pregnancyPeriod", orderFormFieldRules);
    const firstTestTypeRequired = isFieldEnabledByRule("firstTestType", orderFormFieldRules);
    const pregnancyPeriodValid = !pregnancyPeriodRequired || state.orderDNATestForm.step3.pregnancyPeriod.valid;
    const firstTestTypeValid = !firstTestTypeRequired || state.orderDNATestForm.step3.firstTestType.valid;

    return testTypeValidation && legalStatusValid && researchPeriodValid && pregnancyPeriodValid && firstTestTypeValid;
};

export const isSecondStepCompletedSelector = (state: RootState) => {
    const participants = state.orderDNATestForm.step2.testParticipants;
    const orderFormFieldRules = state.orderDNATestForm.orderFormFieldRules;
    const testType = state.orderDNATestForm.step1.testType.value;
    const testTypeAlias = getTestTypeAlias(testType, state.orderDNATestForm.initialSelections?.analyses ?? []);
    const isKinshipTestType = testTypeAlias === "rodstvo" || testTypeAlias === "kinship";
    const canShowKinshipFields = isKinshipCategoryFlowEnabled({
        isKinshipTest: isKinshipTestType,
        participantCount: participants.length,
        participantsMin: state.orderDNATestForm.participantsMin,
    });

    const attributeRequired = testType !== "";
    const attributeValid = !attributeRequired || state.orderDNATestForm.step1.attribute.valid;

    const describeKinshipTiesRequired = canShowKinshipFields && state.orderDNATestForm.step3.matchingRules.length === 0;
    const describeKinshipTiesValid = !describeKinshipTiesRequired || state.orderDNATestForm.step3.describeKinshipTies.valid;

    const kinshipSchemeFieldsValid = !canShowKinshipFields
        || state.orderDNATestForm.step3.matchingRules.length === 0
        || (state.orderDNATestForm.step3.kinshipScheme !== "" && state.orderDNATestForm.step3.familyStepInDoubt !== null);

    const minimumCountOfParticipants = state.orderDNATestForm.participantsMin;
    const participantsMinValid = minimumCountOfParticipants === null
        || minimumCountOfParticipants === infiniteCount
        || participants.length >= minimumCountOfParticipants;
    const maximumCountOfParticipants = state.orderDNATestForm.participantsMax;
    const participantsMaxValid = maximumCountOfParticipants === null
        || maximumCountOfParticipants === infiniteCount
        || participants.length <= maximumCountOfParticipants;
    const minimumCountOfSamples = state.orderDNATestForm.sampleMin;
    const requiredParticipantsCount = minimumCountOfParticipants === null
    || minimumCountOfParticipants === infiniteCount
        ? participants.length
        : minimumCountOfParticipants;
    const samplesMinValid = minimumCountOfSamples === null
        || minimumCountOfSamples === infiniteCount
        || participants.filter(participant => participant.sampleType.length >= minimumCountOfSamples).length >= requiredParticipantsCount;
    const maximumCountOfSamples = state.orderDNATestForm.sampleMax;
    const samplesMaxValid = maximumCountOfSamples === null
      || maximumCountOfSamples === infiniteCount
      || participants.every(participant => participant.sampleType.length <= maximumCountOfSamples);
    const participantSamplePartsValid = participants.every(participant =>
        areParticipantSamplePartsValid(participant.sampleType)
        && areParticipantSamplePartsValid(participant.sampleTypeNotInWork, false)
    );
    const requiredSampleValidation = requiredSampleValidationSelector(state);
    const availableSampleIds = state.orderDNATestForm.step2.samplesVariants.map(sample => sample.id);
    const participantSamplesAreAllowed = participants.every(participant =>
        getInvalidParticipantSamples(
            {
                sampleType: participant.sampleType,
                sampleTypeNotInWork: participant.sampleTypeNotInWork,
            },
            availableSampleIds,
        ).length === 0
    );

    return participants.length > 0
        && participantsMinValid
        && participantsMaxValid
        && samplesMinValid
        && samplesMaxValid
      && participantSamplePartsValid
        && requiredSampleValidation.isValid
        && participantSamplesAreAllowed
        && attributeValid
        && describeKinshipTiesValid
        && kinshipSchemeFieldsValid;
}

function isMeaningOfFilesValid(state: any) {
    let valid = true;
    state.orderDNATestForm.step3.files.forEach((file: UploadingFileInterface) => {
        if ("file" in file && file.file !== null && file.meaning === "") {
            valid = false;
        }
    });
    return valid;
}

//@ts-ignore
export const isThirdStepValidSelector = (state: RootState) => {
    const totalSizeMb = getChosenFilesTotalSize(state) / 1000000;
    const filesTotalSizeValid = totalSizeMb <= OrderDNATestFormConfig.maxFilesSizeMb;

    const chosenFilesCount = getChosenFilesCount(state);
    const filesCountValid = chosenFilesCount <= OrderDNATestFormConfig.maxDocumentsCount;

    const filesMeaningIsValid = isMeaningOfFilesValid(state);

    return filesTotalSizeValid && filesCountValid && filesMeaningIsValid;
}

//TODO здесь 39 получать более надежным способом, и в форме редактирования тоже самое
//@ts-ignore
export const isParticipantFormValidSelector = (state: RootState) => {
    const fioOrNicknameValid = state.orderDNATestForm.step2.fioOrNickname.valid;
    // const genderValid = state.orderDNATestForm.step2.gender.value !== notSelectedId;
    const birthdayValid = state.orderDNATestForm.step2.birthday.valid;

    const degreeOfKinshipValid = state.orderDNATestForm.step2.degreeOfKinship.valid;
    const degreeOfKinshipPreviousOrderNumberValid = state.orderDNATestForm.step2.degreeOfKinshipPreviousOrderNumber.valid;
    const degreeOfKinshipValidation = (degreeOfKinshipValid && state.orderDNATestForm.step2.degreeOfKinship.value !== 39) || (degreeOfKinshipValid && degreeOfKinshipPreviousOrderNumberValid);

    const sampleTypeValid = state.orderDNATestForm.step2.sampleType.valid;
    const sampleTypeOtherValid = state.orderDNATestForm.step2.sampleTypeOther.valid;
    const sampleTypeValidation = (sampleTypeValid && !hasNonStandardSample(state.orderDNATestForm.step2.sampleTypeNotInWork.value)) || (sampleTypeValid && sampleTypeOtherValid);
    const samplePartsValid = areParticipantSamplePartsValid([
        ...state.orderDNATestForm.step2.sampleType.value,
    ]) && areParticipantSamplePartsValid(
        state.orderDNATestForm.step2.sampleTypeNotInWork.value,
        false,
    );

    const sampleCollectionDateValid = state.orderDNATestForm.step2.sampleCollectionDate.valid;

    // const whoCollectedSamplesValid = state.orderDNATestForm.step2.whoCollectedSamples.valid;

    // const documentTypeValid = state.orderDNATestForm.step2.documentType.value !== notSelectedId;
    // const documentNumberValid = state.orderDNATestForm.step2.documentNumber.value.trim() !== "";

    const profileResearchValid = state.orderDNATestForm.step2.profileResearch.valid

    const readyProfileValid = state.orderDNATestForm.step2.readyProfile.valid

    return fioOrNicknameValid &&
        // genderValid &&
        birthdayValid &&
        degreeOfKinshipValidation &&
        sampleTypeValidation &&
        samplePartsValid &&
        profileResearchValid &&
        readyProfileValid &&
        sampleCollectionDateValid
        // whoCollectedSamplesValid &&
        // documentTypeValid &&
        // documentNumberValid
        ;
}

//@ts-ignore
export const participantFormStateSelector = state => {
    return state.orderDNATestForm.step2.isUserNewOrEdit;
}

//@ts-ignore
export const filesSelector = state => {
    return state.orderDNATestForm.step3.files;
};

//@ts-ignore
export const participantPhotosSelector = state => {
    return state.orderDNATestForm.step2.participantPhotos.value;
};
//@ts-ignore
export const identityDocumentsSelector = state => {
    return state.orderDNATestForm.step2.identityDocuments.value;
};
//@ts-ignore
export const informedConsentDocumentsSelector = state => {
    return state.orderDNATestForm.step2.informedConsentDocuments.value;
};
export const readyProfileSelector = (state: RootState) => {
    return state.orderDNATestForm.step2.readyProfile.value;
};
//@ts-ignore
export const fileByIdSelector = id => state => {
    const found = state.orderDNATestForm.step3.files.find((file: UploadingFileInterface) => {
        return file.id === id;
    });
    if (found === undefined) {
        throw new Error(`File with id: ${id} not found.`);
    }
    return found;
}

function getChosenFilesCount(state: any) {
    let count = 0;
    state.orderDNATestForm.step3.files.forEach((file: UploadingFileInterface) => {
        if ("file" in file && file.file !== null) {
            count++;
        }
    });
    return count;
}

//@ts-ignore
export const chosenFilesCountSelector = state => {
    return getChosenFilesCount(state);
};

function getChosenFilesTotalSize(state: any) {
    let size = 0;
    state.orderDNATestForm.step3.files.forEach((file: UploadingFileInterface) => {
        if ("file" in file && file.file !== null) {
            size += file.file.size;
        }
    });
    return size;
}

//@ts-ignore
export const chosenFilesSizeSelector = state => {
    return getChosenFilesTotalSize(state);
};

//@ts-ignore
export const orderDNASendingStatusSelector = state => {
    return state.orderDNATestForm.orderSendingStatus;
};

export const beforeCourtCorrectnessSelector = (state: RootState) => {
    const testParticipants = state.orderDNATestForm.step2.testParticipants;
    const files = state.orderDNATestForm.step3.files;

    // Проверяем, что у каждого участника есть хотя бы одно фото
    const hasAllParticipantPhotos = testParticipants.every((participant: TestParticipantInterface) =>
        participant.participantPhotos &&
        participant.participantPhotos.length > 0 &&
        participant.participantPhotos.some((photo: UploadingFileInterface) => photo.file !== null)
    );

    // Проверяем, что у каждого участника есть хотя бы один документ
    const hasAllIdentityDocuments = testParticipants.every((participant: TestParticipantInterface) =>
        participant.identityDocuments &&
        participant.identityDocuments.length > 0 &&
        participant.identityDocuments.some((doc: UploadingFileInterface) => doc.file !== null)
    );

    // Проверяем, что есть файл с meaning: "protocol_get" и file !== null
    const hasProtocolGetFile = files.some((file: UploadingFileInterface) =>
        file.meaning === "protocol_get" && file.file !== null
    );

    return hasAllParticipantPhotos &&
        hasAllIdentityDocuments &&
        hasProtocolGetFile;
};

export const courtOrderCorrectnessSelector = (state: RootState) => {
    const testParticipants = state.orderDNATestForm.step2.testParticipants;
    const files = state.orderDNATestForm.step3.files;
    const civilCaseNumber = state.orderDNATestForm.step3.civilCaseNumber.value;
    const courtName = state.orderDNATestForm.step3.courtName.value;

    // Проверяем, что у каждого участника есть хотя бы одно фото
    const hasAllParticipantPhotos = testParticipants.every((participant: TestParticipantInterface) =>
        participant.participantPhotos &&
        participant.participantPhotos.length > 0 &&
        participant.participantPhotos.some((photo: UploadingFileInterface) => photo.file !== null)
    );

    // Проверяем, что у каждого участника есть хотя бы один документ
    const hasAllIdentityDocuments = testParticipants.every((participant: TestParticipantInterface) =>
        participant.identityDocuments &&
        participant.identityDocuments.length > 0 &&
        participant.identityDocuments.some((doc: UploadingFileInterface) => doc.file !== null)
    );

    // Проверяем, что есть файл с meaning: "protocol_get" и file !== null
    const hasProtocolGetFile = files.some((file: UploadingFileInterface) =>
        file.meaning === "protocol_get" && file.file !== null
    );

    // Проверяем, что есть файл с meaning: "determination_court" и file !== null
    const hasDeterminationCourtFile = files.some((file: UploadingFileInterface) =>
        file.meaning === "determination_court" && file.file !== null
    );

    // Проверка на наличие обоих файлов (протокл забора + определение суда)
    const hasProtocolAndDeterminationFiles = hasProtocolGetFile && hasDeterminationCourtFile;
    // проверка на заполненность поля "Номер гражданского дела"
    const isCivilCaseNumberFilled = !!civilCaseNumber;
    // проверка на заполненность поля "Название суда"
    const isCourtNameFilled = !!courtName;
    // ЛИБО заполнены файлы ЛИБО заполнены поля (или и то и то)
    const hasFilesOrCourtFieldsFilled = hasProtocolAndDeterminationFiles || (isCivilCaseNumberFilled && isCourtNameFilled)

    return hasAllParticipantPhotos &&
        hasAllIdentityDocuments &&
        hasFilesOrCourtFieldsFilled;
};

export const checkedOrderSelector = (state: RootState) => state.orderDNATestForm.checkedOrder.value;
export const kinshipSchemaConfirmedSelector = (state: RootState) => state.orderDNATestForm.kinshipSchemaConfirmed;

export const getSelectorForInitialSelections = () => (state: RootState) => {
    return state.orderDNATestForm.initialSelections;
};

export const getSelectorForOrderPrice = () => (state: RootState) => {
    return state.orderDNATestForm.orderPrice;
};

export const partnerFormNotEmptySelector = (state: RootState) => {

    const fioOrNickname = state.orderDNATestForm.step2.fioOrNickname.value.trim();
    const gender = state.orderDNATestForm.step2.gender.value;
    const birthday = state.orderDNATestForm.step2.birthday.value.trim();
    const degreeOfKinship = state.orderDNATestForm.step2.degreeOfKinship.value;
    const degreeOfKinshipPreviousOrderNumber = state.orderDNATestForm.step2.degreeOfKinshipPreviousOrderNumber.value.trim();
    const sampleType = state.orderDNATestForm.step2.sampleType.value;
    const sampleTypeOther = state.orderDNATestForm.step2.sampleTypeOther.value.trim();
    const sampleCollectionDate = state.orderDNATestForm.step2.sampleCollectionDate.value.trim();
    const whoCollectedSamples = state.orderDNATestForm.step2.whoCollectedSamples.value;
    const documentType = state.orderDNATestForm.step2.documentType.value;
    const documentNumber = state.orderDNATestForm.step2.documentNumber.value.trim();

    return fioOrNickname !== "" ||
        gender !== notSelectedId ||
        birthday !== "" ||
        degreeOfKinship !== notSelectedId ||
        degreeOfKinshipPreviousOrderNumber !== "" ||
        sampleType.length > 0 ||
        sampleTypeOther !== "" ||
        sampleCollectionDate !== "" ||
        whoCollectedSamples !== notSelectedId ||
        documentType !== notSelectedId ||
        documentNumber !== ""
        ;
};

export const participantsMinSelector = (state: RootState) => {
    return state.orderDNATestForm.participantsMin;
};

export const participantsMaxSelector = (state: RootState) => {
    return state.orderDNATestForm.participantsMax;
};

export const sampleMinSelector = (state: RootState) => {
    return state.orderDNATestForm.sampleMin;
};

export const sampleMaxSelector = (state: RootState) => {
    return state.orderDNATestForm.sampleMax;
};

export const countOfParticipantsSelector = (state: RootState) => state.orderDNATestForm.step2.testParticipants.length

export const samplesVariantsSelector = (state: RootState) => {
    return state.orderDNATestForm.step2.samplesVariants;
};

export const selectionsFetchingSelector = (state: RootState) => {
    return state.orderDNATestForm.step2.isSelectionsFetching;
};

export const selectionsSuccessSelector = (state: RootState) => {
    return state.orderDNATestForm.step2.isSelectionsSuccess;
};

export const matchingRulesSelector = (state: RootState) => state.orderDNATestForm.step3.matchingRules
export const orderFormFieldRulesSelector = (state: RootState) => state.orderDNATestForm.orderFormFieldRules

export const matchingRuleIdSelector = (state: RootState) => state.orderDNATestForm.step3.matchingRuleId;

export const kinshipSchemeSelector = (state: RootState) => state.orderDNATestForm.step3.kinshipScheme

export const familyStepInDoubtSelector = (state: RootState) => state.orderDNATestForm.step3.familyStepInDoubt

const kinshipAliases = ["rodstvo", "kinship"];

export const isKinshipTestTypeSelectedSelector = (state: RootState): boolean => {
    const testTypeId = state.orderDNATestForm.step1.testType.value;
    const analyses = state.orderDNATestForm.initialSelections?.analyses ?? [];
    const selected = analyses.find(item => String(item.analyse.id) === String(testTypeId));
    return kinshipAliases.includes(selected?.analyse.alias ?? "");
};

export const legalStatusAliasSelector = (state: RootState): string => {
    const id = state.orderDNATestForm.step1.legalStatus.value;
    const statuses = state.orderDNATestForm.initialSelections?.legal_status ?? [];
    return statuses.find(s => String(s.id) === String(id))?.alias ?? "";
};

// CategorySelector selectors
export const categorySelectionPathSelector = (state: RootState) =>
    state.orderDNATestForm.step3.categorySelectionPath;

export const currentCategoryStepSelector = (state: RootState) =>
    state.orderDNATestForm.step3.currentCategoryStep;

export const finalSelectedRuleSelector = (state: RootState) =>
    state.orderDNATestForm.step3.finalSelectedRule;

export const kinshipCategoryRuleIdSelector = (state: RootState) =>
    state.orderDNATestForm.step3.finalSelectedRule?.id ?? null;

export const kinshipSchemaJsonSelector = (state: RootState) =>
    state.orderDNATestForm.step3.kinshipSchemaJson;

export const isFinalStepSelector = (state: RootState) =>
    state.orderDNATestForm.step3.finalSelectedRule !== null;

export const OrderDNATestFormReducer = OrderDNATestFormSlice.reducer;

export interface UploadingFileInterface {
    id: string,
    file: { name: string, size: number } | null,
    meaning: string,
}

export interface TestParticipantInterface {
    id: string,
    fioOrNickname: string,
    gender: number,
    birthday: string,
    degreeOfKinship: number,
    degreeOfKinshipPreviousOrderNumber: string,
    sampleType: SampleTypeInterface[],
    sampleTypeNotInWork: SampleTypeInterface[],
    sampleTypeOther: string,
    sampleCollectionDate: string,
    documentType: number,
    documentNumber: string,
    whoCollectedSamples: number,
    isExternalSampling: boolean,
    participantPhotos: UploadingFileInterface[],
    identityDocuments: UploadingFileInterface[],
    informedConsentDocuments: UploadingFileInterface[],
    readyProfile: UploadingFileInterface[],
    profileResearch: number,
}

export type userNewOrEdit = "new" | "edit";

export interface updateMatchingRulePayloadAction {
    kinshipScheme: string,
    familyStepInDoubt: FamilyStepsActiveItemInterface | null,
    attribute: string
}
